import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Send,
    XCircle,
    Copy,
    ArrowRight,
    MailCheck,
    Mail,
    Clock,
    MapPin,
    User as UserIcon,
    FileText,
    ExternalLink,
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { PageHeader, LoadingPage, ErrorPage, StatusPill } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Textarea, Input } from '../../components/ui/Form.jsx';
import EmailPreviewModal from '../../components/email/EmailPreviewModal.jsx';

// =============================================================================
// QuoteDetailPage — Slice 7d.4 quote tool
// =============================================================================
//
// Single-page detail view for one quote. Read-only display of rides + pricing
// + recipient + audit. Action buttons depend on status:
//
//   draft     → Send email, Edit (parked — see note), Cancel, Duplicate
//   sent      → Resend email, Convert to booking, Cancel, Duplicate
//   converted → "View booking" link, Duplicate
//   expired   → Duplicate
//   cancelled → Duplicate
//
// Edit-while-draft is supported by the backend but we don't surface a UI
// for it in this slice. Editing rides means re-running the entire price
// calculation flow which is what NewBookingPage already does. For now,
// drafts that need fixing get cancelled + duplicated, then the duplicate
// is edited on the standard quote-mode form.
//
// "Convert to booking" navigates to /bookings/new?quote_token=XXX. The
// prefill flow on NewBookingPage is a separate slice — for now the link
// just preserves the token in the URL so when prefill ships, links from
// converted quotes still work.
// =============================================================================

const STATUS_CONFIG = {
    draft:     { label: 'Draft',     pillStatus: 'neutral' },
    sent:      { label: 'Sent',      pillStatus: 'info'    },
    converted: { label: 'Converted', pillStatus: 'success' },
    expired:   { label: 'Expired',   pillStatus: 'warning' },
    cancelled: { label: 'Cancelled', pillStatus: 'neutral' },
};

const VEHICLE_CLASS_LABELS = {
    business: 'Business Class',
    first: 'First Class',
    suv: 'First Class SUV',
    xl: 'Business Class XL',
};

function formatTimestamp(ts) {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function formatRelativeTime(ts) {
    if (!ts) return '';
    const now = new Date();
    const then = new Date(ts);
    if (isNaN(then.getTime())) return '';
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
    const dayDiff = Math.round((today - thenDay) / 86400000);
    const timeStr = then.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    if (dayDiff === 0) return timeStr;
    if (dayDiff === 1) return `Yesterday at ${timeStr}`;
    return then.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }) + ` at ${timeStr}`;
}

function formatRidePickup(dateStr, timeStr) {
    if (!dateStr) return '—';
    const slice = String(dateStr).slice(0, 10);
    const d = new Date(`${slice}T${timeStr || '00:00'}`);
    if (isNaN(d.getTime())) return slice;
    return d.toLocaleDateString('en-GB', {
        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    }) + ' · ' + (timeStr ? String(timeStr).slice(0, 5) : '—');
}

// Same total resolution as the list page.
function ridePriceLabel(pricing) {
    const t = pricing?.total ?? pricing?.gross ?? pricing?.price ?? pricing?.amount;
    if (t == null) return '—';
    return `£${Number(t).toFixed(2)}`;
}

function masterTotalLabel(quote) {
    if (quote.master_total != null) {
        return `£${Number(quote.master_total).toFixed(2)}`;
    }
    const pricings = Array.isArray(quote.pricings) ? quote.pricings : [];
    if (pricings.length === 1) {
        const p = pricings[0];
        const t = p?.total ?? p?.gross ?? p?.price ?? p?.amount;
        if (t != null) return `£${Number(t).toFixed(2)}`;
    }
    if (pricings.length > 1) {
        const sum = pricings.reduce((s, p) => {
            const t = p?.total ?? p?.gross ?? p?.price ?? p?.amount ?? 0;
            return s + Number(t || 0);
        }, 0);
        if (sum > 0) return `£${sum.toFixed(2)}`;
    }
    return '—';
}

export default function QuoteDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = useApi();
    const queryClient = useQueryClient();

    const [showCancel, setShowCancel] = useState(false);
    const [showSendPreview, setShowSendPreview] = useState(false);

    const { data: quote, isLoading, isError, error } = useQuery({
        queryKey: ['quote', id],
        queryFn: () => api.get(`/quotes/${id}`),
    });

    const duplicateMutation = useMutation({
        mutationFn: () => api.post(`/quotes/${id}/duplicate`),
        onSuccess: (data) => {
            const newId = data?.quote?.id;
            if (newId) {
                toast.success('Quote duplicated as new draft');
                navigate(`/quotes/${newId}`);
            } else {
                toast.success('Quote duplicated');
                queryClient.invalidateQueries({ queryKey: ['quotes', 'list'] });
            }
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not duplicate';
            toast.error(msg);
        },
    });

    if (isLoading) return <LoadingPage label="Loading quote…" />;
    if (isError) return <ErrorPage error={error} />;
    if (!quote) return null;

    const cfg = STATUS_CONFIG[quote.status] || { label: quote.status, pillStatus: 'neutral' };
    const rides = Array.isArray(quote.rides) ? quote.rides : [];
    const pricings = Array.isArray(quote.pricings) ? quote.pricings : [];
    const isMultiRide = rides.length > 1;

    const nowExpired = quote.status === 'sent'
        && quote.expires_at
        && new Date(quote.expires_at).getTime() < Date.now();
    const effectiveStatus = nowExpired ? 'expired' : quote.status;
    const effectiveCfg = STATUS_CONFIG[effectiveStatus];

    function handleConvert() {
        if (!quote.token) {
            toast.error('Quote has no token — cannot convert');
            return;
        }
        navigate(`/bookings/new?quote_token=${encodeURIComponent(quote.token)}`);
    }

    return (
        <div>
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-3 flex-wrap">
                        <FileText className="w-5 h-5 text-ink-subtle" />
                        <span className="font-mono text-base">{quote.quote_ref}</span>
                        <StatusPill status={effectiveCfg.pillStatus}>{effectiveCfg.label}</StatusPill>
                        {isMultiRide && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200">
                                {rides.length} rides
                            </span>
                        )}
                    </span>
                }
                subtitle={
                    <span className="text-sm">
                        <button
                            type="button"
                            onClick={() => {
                                if (window.history.length > 1) navigate(-1);
                                else navigate('/quotes');
                            }}
                            className="text-ink-muted hover:text-ink inline-flex items-center gap-1"
                        >
                            <ArrowLeft className="h-3 w-3" />
                            Quotes
                        </button>
                        {quote.linked_customer_name && quote.customer_id && (
                            <>
                                <span className="text-ink-muted mx-2">·</span>
                                <Link
                                    to={`/customers/${quote.customer_id}`}
                                    className="text-ink-muted hover:text-ink"
                                >
                                    {quote.linked_customer_name}
                                </Link>
                            </>
                        )}
                    </span>
                }
            />

            {/* Action panel — what dispatcher can do given current status */}
            <div className="card mb-5">
                <div className="card-body">
               <ActionPanel
                        quote={quote}
                        effectiveStatus={effectiveStatus}
                        onSend={() => setShowSendPreview(true)}
                        sendPending={false}
                        onResend={() => setShowSendPreview(true)}
                        onConvert={handleConvert}
                        onCancel={() => setShowCancel(true)}
                        onDuplicate={() => duplicateMutation.mutate()}
                        duplicatePending={duplicateMutation.isPending}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    <QuoteRidesCard rides={rides} pricings={pricings} masterTotal={masterTotalLabel(quote)} isMultiRide={isMultiRide} />
                </div>

                <div className="lg:col-span-1 space-y-5">
                    <RecipientCard quote={quote} />
                    <AuditCard quote={quote} />
                </div>
            </div>

{showSendPreview && (
                <EmailPreviewModal
                    emails={[{
                        id: 'quote',
                        label: effectiveStatus === 'draft' ? 'Send quote' : 'Resend quote',
                        previewParams: { kind: 'quote', quote_id: quote.id },
                        sendEndpoint: effectiveStatus === 'draft'
                            ? `/quotes/${quote.id}/send`
                            : `/quotes/${quote.id}/resend`,
                        sendBody: (recipients) => effectiveStatus === 'draft'
                            ? {
                                recipients,
                                customer_email: quote.customer_email,
                                customer_name: quote.customer_name || null,
                            }
                            : { recipients },
                    }]}
                    title={effectiveStatus === 'draft' ? 'Send quote' : 'Resend quote'}
                    description={`Quote ${quote.quote_ref} \u2014 review before sending.`}
                    onClose={() => setShowSendPreview(false)}
                    onSent={() => {
                        setShowSendPreview(false);
                        queryClient.invalidateQueries({ queryKey: ['quote', id] });
                        queryClient.invalidateQueries({ queryKey: ['quotes', 'list'] });
                    }}
                />
            )}

            {showCancel && (
                <CancelQuoteModal
                    quote={quote}
                    onClose={() => setShowCancel(false)}
                />
            )}
        </div>
    );
}

// =============================================================================
// ActionPanel — context-aware buttons based on quote status
// =============================================================================

function ActionPanel({
    quote, effectiveStatus,
    onSend, sendPending,
    onResend,
    onConvert,
    onCancel,
    onDuplicate, duplicatePending,
}) {
    const isDraft = effectiveStatus === 'draft';
    const isSent = effectiveStatus === 'sent';
    const isConverted = effectiveStatus === 'converted';
    const isExpired = effectiveStatus === 'expired';
    const isCancelled = effectiveStatus === 'cancelled';

    return (
        <div className="space-y-3">
            <div className="text-xs uppercase text-ink-subtle tracking-wide font-medium">
                Status
            </div>
            <div className="text-2xl font-semibold text-ink">
                {STATUS_CONFIG[effectiveStatus]?.label || effectiveStatus}
            </div>

            {/* Status-specific subtext */}
            {isDraft && (
                <p className="text-xs text-ink-subtle">
                    Not sent yet. Click Send to email it now.
                </p>
            )}
            {isSent && quote.sent_at && (
                <p className="text-xs text-ink-subtle">
                    Sent {formatRelativeTime(quote.sent_at)} to {quote.customer_email}
                </p>
            )}
            {isExpired && (
                <p className="text-xs text-ink-subtle">
                    Customer can no longer convert this quote. Duplicate to create a fresh one.
                </p>
            )}
            {isConverted && quote.converted_to_booking_id && (
                <p className="text-xs text-ink-subtle">
                    Converted to booking{' '}
                    <Link
                        to={`/bookings/${quote.converted_to_booking_id}`}
                        className="text-ink-info hover:underline font-mono"
                    >
                        {quote.converted_booking_ref || 'view'}
                    </Link>
                    {' '}{formatRelativeTime(quote.converted_at)}
                </p>
            )}
            {isCancelled && (
                <p className="text-xs text-ink-subtle">
                    Cancelled {formatRelativeTime(quote.cancelled_at)}
                    {quote.cancellation_reason && (
                        <> — <span className="italic">{quote.cancellation_reason}</span></>
                    )}
                </p>
            )}

            {/* Action buttons */}
            <div className="pt-3 border-t border-paper-border-soft flex flex-wrap gap-2">
                {isDraft && (
                    <Button variant="primary" onClick={onSend} loading={sendPending}>
                        <Send className="h-3.5 w-3.5" />
                        Send email
                    </Button>
                )}

                {isSent && (
                    <>
                        <Button variant="primary" onClick={onConvert}>
                            <ArrowRight className="h-3.5 w-3.5" />
                            Convert to booking
                        </Button>
                        <Button variant="secondary" onClick={onResend}>
                            <MailCheck className="h-3.5 w-3.5" />
                            Resend email
                        </Button>
                    </>
                )}

                {isConverted && quote.converted_to_booking_id && (
                    <Link
                        to={`/bookings/${quote.converted_to_booking_id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-sm"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View booking
                    </Link>
                )}

                {(isDraft || isSent) && (
                    <Button variant="secondary" onClick={onCancel} className="text-status-danger">
                        <XCircle className="h-3.5 w-3.5" />
                        Cancel
                    </Button>
                )}

                <Button variant="secondary" onClick={onDuplicate} loading={duplicatePending}>
                    <Copy className="h-3.5 w-3.5" />
                    Duplicate
                </Button>
            </div>
        </div>
    );
}

// =============================================================================
// QuoteRidesCard — read-only ride + pricing display
// =============================================================================

function QuoteRidesCard({ rides, pricings, masterTotal, isMultiRide }) {
    if (rides.length === 0) {
        return (
            <div className="card">
                <div className="card-body">
                    <p className="text-sm text-ink-subtle italic">No rides on this quote.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">
                    {isMultiRide ? 'Itinerary' : 'Ride'}
                </h2>
            </div>
            <div className="card-body space-y-4">
                {rides.map((ride, i) => (
                    <RideBlock
                        key={i}
                        ride={ride}
                        pricing={pricings[i]}
                        index={i}
                        total={rides.length}
                        isMultiRide={isMultiRide}
                    />
                ))}

                {isMultiRide && (
                    <div className="pt-4 mt-2 border-t-2 border-ink/80 flex items-center justify-between">
                        <span className="text-xs uppercase tracking-[0.1em] text-ink font-bold">
                            Grand total
                        </span>
                        <span className="text-xl font-semibold text-ink">
                            {masterTotal}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

function RideBlock({ ride, pricing, index, total, isMultiRide }) {
    const isHourly = ride.mode === 'hourly';
    const stops = Array.isArray(ride.stops) ? ride.stops : [];
    const vehicleLabel = ride.vehicle_class_label || VEHICLE_CLASS_LABELS[ride.vehicle_class] || ride.vehicle_class || '—';

    return (
        <div className={isMultiRide ? 'rounded-lg border border-paper-border-soft p-4' : ''}>
            {isMultiRide && (
                <div className="text-xs font-bold tracking-wider text-ink-subtle uppercase mb-3">
                    Ride {index + 1} of {total}
                </div>
            )}

            <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-ink-subtle" />
                <span className="text-base font-semibold text-ink">
                    {formatRidePickup(ride.pickup_date, ride.pickup_time)}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold bg-canvas text-ink-muted border border-paper-border-soft">
                    {isHourly ? 'Hourly' : 'One-way'}
                </span>
            </div>

            <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                    <div>
                        <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">Pickup</div>
                        <div className="text-ink">{ride.pickup_address}</div>
                    </div>
                </div>

                {isHourly && stops.map((stop, i) => (
                    <div key={i} className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0 opacity-60" />
                        <div>
                            <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                                Stop {i + 1}
                                {stop.time && <span className="ml-2 normal-case tracking-normal">{String(stop.time).slice(0, 5)}</span>}
                            </div>
                            <div className="text-ink">{stop.address}</div>
                        </div>
                    </div>
                ))}

                <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                    <div>
                        <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                            {isHourly ? 'Final destination' : 'Drop off'}
                            {isHourly && ride.final_time && (
                                <span className="ml-2 normal-case tracking-normal">{String(ride.final_time).slice(0, 5)}</span>
                            )}
                        </div>
                        <div className="text-ink">{ride.dropoff_address}</div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 text-xs">
                    <div>
                        <div className="text-ink-subtle uppercase tracking-wide font-medium">Service</div>
                        <div className="text-ink">
                            {isHourly
                                ? `Hourly · ${ride.hours || 2} hour${(ride.hours || 2) !== 1 ? 's' : ''}`
                                : 'One-way'}
                        </div>
                    </div>
                    <div>
                        <div className="text-ink-subtle uppercase tracking-wide font-medium">Vehicle</div>
                        <div className="text-ink">{vehicleLabel}</div>
                    </div>
                </div>

                {(ride.flight_number || ride.tail_number || ride.fbo_code) && (
                    <div className="grid grid-cols-3 gap-3 pt-2 text-xs">
                        {ride.flight_number && (
                            <div>
                                <div className="text-ink-subtle uppercase tracking-wide font-medium">Flight</div>
                                <div className="text-ink">{ride.flight_number}</div>
                            </div>
                        )}
                        {ride.tail_number && (
                            <div>
                                <div className="text-ink-subtle uppercase tracking-wide font-medium">Tail</div>
                                <div className="text-ink">{ride.tail_number}</div>
                            </div>
                        )}
                        {ride.fbo_code && (
                            <div>
                                <div className="text-ink-subtle uppercase tracking-wide font-medium">FBO</div>
                                <div className="text-ink">{ride.fbo_code}</div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Per-ride total — bottom of card. For single-ride quotes this
                is the total; for multi-ride it's just this ride's portion
                (grand total renders in the parent). */}
            <div className="pt-3 mt-3 border-t border-paper-border-soft flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-ink-subtle font-medium">
                    {isMultiRide ? 'Ride total' : 'Total'}
                </span>
                <span className={`font-semibold text-ink ${isMultiRide ? 'text-base' : 'text-lg'}`}>
                    {ridePriceLabel(pricing)}
                </span>
            </div>
        </div>
    );
}

// =============================================================================
// RecipientCard — who's getting the quote
// =============================================================================

function RecipientCard({ quote }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                    <Mail className="h-4 w-4 text-ink-subtle" />
                    Recipient
                </h2>
            </div>
            <div className="card-body space-y-3">
                {quote.customer_name && (
                    <div className="flex items-start gap-2">
                        <UserIcon className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">Name</div>
                            <div className="text-sm text-ink">{quote.customer_name}</div>
                        </div>
                    </div>
                )}
                <div className="flex items-start gap-2">
                    <Mail className="h-4 w-4 text-ink-subtle mt-0.5 flex-shrink-0" />
                    <div>
                        <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">Email</div>
                        <div className="text-sm text-ink">{quote.customer_email || '—'}</div>
                    </div>
                </div>
                {quote.linked_customer_name && quote.customer_id && (
                    <div className="pt-2 border-t border-paper-border-soft text-xs text-ink-subtle">
                        Linked to{' '}
                        <Link
                            to={`/customers/${quote.customer_id}`}
                            className="text-ink-info hover:underline"
                        >
                            {quote.linked_customer_name}
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// AuditCard — created/sent/expires/converted/cancelled timeline
// =============================================================================

function AuditCard({ quote }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Timeline</h2>
            </div>
            <div className="card-body space-y-3 text-sm">
                <Row
                    label="Created"
                    value={formatTimestamp(quote.created_at)}
                    detail={quote.created_by_email}
                />
                {quote.sent_at && (
                    <Row label="Sent" value={formatTimestamp(quote.sent_at)} />
                )}
                {quote.status === 'sent' && quote.expires_at && (
                    <Row
                        label="Expires"
                        value={formatTimestamp(quote.expires_at)}
                        detail={(() => {
                            const ms = new Date(quote.expires_at).getTime() - Date.now();
                            if (ms <= 0) return 'expired';
                            const days = Math.floor(ms / 86400000);
                            const hours = Math.floor((ms % 86400000) / 3600000);
                            if (days > 0) return `in ${days} day${days !== 1 ? 's' : ''}`;
                            if (hours > 0) return `in ${hours} hour${hours !== 1 ? 's' : ''}`;
                            return 'within the hour';
                        })()}
                    />
                )}
                {quote.converted_at && (
                    <Row
                        label="Converted"
                        value={formatTimestamp(quote.converted_at)}
                        detail={quote.converted_to_booking_id ? 'booking created' : null}
                    />
                )}
                {quote.cancelled_at && (
                    <Row
                        label="Cancelled"
                        value={formatTimestamp(quote.cancelled_at)}
                        detail={quote.cancellation_reason}
                    />
                )}
                {quote.parent_quote_id && (
                    <div className="pt-2 border-t border-paper-border-soft text-xs text-ink-subtle">
                        Duplicated from{' '}
                        <Link
                            to={`/quotes/${quote.parent_quote_id}`}
                            className="text-ink-info hover:underline"
                        >
                            another quote
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ label, value, detail }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-wide text-ink-subtle font-medium">{label}</div>
            <div className="text-ink">{value}</div>
            {detail && <div className="text-xs text-ink-subtle italic">{detail}</div>}
        </div>
    );
}

// =============================================================================
// CancelQuoteModal — manual cancel with required reason
// =============================================================================

function CancelQuoteModal({ quote, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [reason, setReason] = useState('');

    const cancelMutation = useMutation({
        mutationFn: () => api.post(`/quotes/${quote.id}/cancel`, { reason: reason.trim() }),
        onSuccess: () => {
            toast.success(`Quote ${quote.quote_ref} cancelled`);
            queryClient.invalidateQueries({ queryKey: ['quote', quote.id] });
            queryClient.invalidateQueries({ queryKey: ['quotes', 'list'] });
            onClose();
        },
        onError: (err) => {
            const msg = err?.response?.data?.error || err?.message || 'Could not cancel';
            toast.error(msg);
        },
    });

    const canSubmit = reason.trim().length > 0 && !cancelMutation.isPending;

    return (
        <Modal
            open
            onClose={cancelMutation.isPending ? undefined : onClose}
            title="Cancel quote?"
            description={`This will cancel ${quote.quote_ref}. The customer's "Book now" link will stop working. Not undoable.`}
            size="md"
        >
            <div className="mt-2">
                <label className="block text-sm font-medium text-ink mb-1.5">
                    Reason <span className="text-red-500">*</span>
                </label>
                <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. customer changed plans, replaced by new quote with different pricing"
                    rows={3}
                    disabled={cancelMutation.isPending}
                    autoFocus
                />
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={cancelMutation.isPending}
                >
                    Back
                </Button>
                <Button
                    variant="danger"
                    onClick={() => cancelMutation.mutate()}
                    disabled={!canSubmit}
                    loading={cancelMutation.isPending}
                >
                    <XCircle className="h-4 w-4" />
                    Cancel quote
                </Button>
            </div>
        </Modal>
    );
}
